import React from 'react';
import BearriBadge, { BearriBadgeName, BearriCue } from './BearriBadge';
import type { BearriMotionName } from './motionAssets';
import './chapterHeading.css';

/** land / run / keep are the three chapters; home is the desk itself (ink-blue, no chapter). */
export type ChapterTone = 'land' | 'run' | 'keep' | 'home';

export interface ChapterHeadingProps {
  title: string;
  subtitle?: React.ReactNode;
  badge?: BearriBadgeName;
  art?: React.ReactNode;
  actions?: React.ReactNode;
  busy?: boolean;
  level?: 1 | 2 | 3;
  titleId?: string;
  size?: 'page' | 'section';
  className?: string;
  /** A cut-paper tab above the title naming the chapter ("Research", "Your sponsors"). */
  eyebrow?: string;
  /** Colours the page eyebrow's stripe — the one place a chapter tone appears inside a page. Never a fill. */
  tone?: ChapterTone;
  /** The clip the pin comes alive with while `busy` (page-level work only). */
  motion?: BearriMotionName;
  /** A one-shot clip for an earned moment (a won deal); `key` changes fire it again. */
  cue?: BearriCue;
}

/** Ink on the desk: pin, tab, title and rule share layout tracks; no absolutely positioned artwork. */
export default function ChapterHeading({ title, subtitle, badge, art, actions, busy,
  level = 2, titleId, size = 'section', className = '', eyebrow, tone, motion, cue }: ChapterHeadingProps) {
  const Heading = `h${level}` as 'h1' | 'h2' | 'h3';
  const hasArt = Boolean(badge || art);
  // a tab that only repeats the title ("OUTREACH / Outreach") is noise; the page name is already the active nav tab
  const showEyebrow = Boolean(eyebrow) && eyebrow!.trim().toLowerCase() !== title.trim().toLowerCase();
  return <div className={`br-chapter-shell ${className}`} data-tone={tone}><div className={`br-chapter-heading br-chapter-heading--${size}${hasArt ? ' br-chapter-heading--illustrated' : ''}${actions ? ' br-chapter-heading--actions' : ''}`}>
    {hasArt && <div className="br-chapter-heading__art" aria-hidden={badge ? undefined : true}>
      {badge ? <BearriBadge name={badge} busy={busy} motion={motion} cue={cue} label={`Spin Bearri — ${title}`} /> : art}
    </div>}
    <div className="br-chapter-heading__band">
      {showEyebrow && <span className="br-tab br-chapter-heading__eyebrow">{eyebrow}</span>}
      <Heading id={titleId} className="br-chapter-heading__title"><span className="br-chapter-heading__label">{title}</span></Heading>
      {subtitle && <div className="br-chapter-heading__description">{subtitle}</div>}
    </div>
    {actions && <div className="br-chapter-heading__actions">{actions}</div>}
  </div></div>;
}
